import React, { useEffect, useState } from 'react';
import {
  Dialog,
  Portal,
  CloseButton,
  Input,
  Button,
  VStack,
  Text,
  Box,
  Textarea,
  Image,
  Flex,
  IconButton,
  HStack,
  SimpleGrid,
  useDisclosure
} from '@chakra-ui/react';
import {
  LuPlus,
  LuTrash2,
  LuArrowLeft,
  LuArrowRight,
  LuArrowUpDown
} from 'react-icons/lu';
import { COLORS } from '../../../theme';
import ReorderGalleryImages from './ReorderGalleryImages';

export default function ImageDialog({
  isOpen,
  onClose,
  mode,
  imageData,
  isSaving,
  onSave
}) {
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    urlPath: '',
    images: []
  });

  const [newImageUrl, setNewImageUrl] = useState('');

  const [manualSlug, setManualSlug] = useState(false);

  const {
    open: isReorderOpen,
    onOpen: reorderOnOpen,
    onClose: reorderOnClose
  } = useDisclosure();

  useEffect(() => {
    if (isOpen) {
      if (imageData) {
        setFormData({
          title: imageData.title || '',
          category: imageData.category || '',
          description: imageData.description || '',
          urlPath: imageData.urlPath || '',
          images: imageData.images || []
        });
        setManualSlug(true);
      } else {
        setFormData({
          title: '',
          category: '',
          description: '',
          urlPath: '',
          images: []
        });
        setManualSlug(false);
      }
      setNewImageUrl('');
    }
  }, [isOpen, imageData]);

  useEffect(() => {
    if (mode === 'create' && !manualSlug && formData.title) {
      const slug = formData.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setFormData((prev) => ({ ...prev, urlPath: slug }));
    }
  }, [formData.title, mode, manualSlug]);

  const handleAddField = () => {
    if (newImageUrl.trim()) {
      setFormData({
        ...formData,
        images: [...formData.images, newImageUrl.trim()]
      });
      setNewImageUrl('');
    }
  };

  const handleRemoveImage = (index) => {
    const updatedImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: updatedImages });
  };

  const moveImage = (index, direction) => {
    const updated = [...formData.images];
    const newIndex = direction === 'left' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= updated.length) return;
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setFormData({ ...formData, images: updated });
  };

  const handleSave = () => {
    onSave(formData);
  };

  const handleApplyReorder = (newOrder) => {
    setFormData({ ...formData, images: newOrder });
  };

  const dialogTitle = mode === 'create' ? 'Add New Category' : 'Edit Category';
  const actionText = mode === 'create' ? 'Add' : 'Save';

  const inputBg = { _light: 'white', _dark: 'gray.900' };
  const borderColor = { _light: 'gray.200', _dark: 'gray.700' };

  return (
    <Dialog.Root
      size="lg"
      placement="center"
      motionPreset="slide-in-bottom"
      open={isOpen}
      onOpenChange={(details) => !details.open && onClose()}
      scrollBehavior="inside"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header borderBottomWidth="1px" borderColor={borderColor}>
              <Dialog.Title>{dialogTitle}</Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Header>

            <Dialog.Body py={6}>
              <VStack gap={4} align="stretch">
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                  <Box>
                    <Text fontSize="xs" fontWeight="bold" mb={1}>
                      TITLE
                    </Text>
                    <Input
                      placeholder="e.g. Wedding Moments"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                      bg={inputBg}
                    />
                  </Box>
                  <Box>
                    <Text fontSize="xs" fontWeight="bold" mb={1}>
                      CATEGORY
                    </Text>
                    <Input
                      placeholder="e.g. Wedding"
                      value={formData.category}
                      onChange={(e) =>
                        setFormData({ ...formData, category: e.target.value })
                      }
                      bg={inputBg}
                    />
                  </Box>
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                  <Box>
                    <Text fontSize="xs" fontWeight="bold" mb={1}>
                      URL PATH (SLUG)
                    </Text>
                    <Input
                      placeholder="e.g. wedding-moments"
                      value={formData.urlPath}
                      onChange={(e) =>
                        setFormData({ ...formData, urlPath: e.target.value })
                      }
                      bg={inputBg}
                      disabled={mode === 'edit'}
                    />
                  </Box>
                </SimpleGrid>

                <Box>
                  <Text fontSize="xs" fontWeight="bold" mb={1}>
                    DESCRIPTION
                  </Text>
                  <Textarea
                    placeholder="Enter a brief description"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    bg={inputBg}
                    rows={3}
                  />
                </Box>

                <Box>
                  <Flex justify="space-between" align="center" mb={2}>
                    <Text fontSize="xs" fontWeight="bold">
                      IMAGES ({formData.images.length})
                    </Text>
                    <HStack gap={2}>
                      <Button
                        size="xs"
                        variant="ghost"
                        colorPalette="orange"
                        leftIcon={<LuArrowUpDown />}
                        onClick={reorderOnOpen}
                        disabled={formData.images.length < 2}
                      >
                        Reorder Images
                      </Button>
                      <Button
                        size="xs"
                        variant="ghost"
                        colorPalette="red"
                        leftIcon={<LuTrash2 />}
                        onClick={() => setFormData({ ...formData, images: [] })}
                        disabled={formData.images.length === 0}
                      >
                        Clear All
                      </Button>
                    </HStack>
                  </Flex>
                  <HStack mb={4}>
                    <Input
                      placeholder="Paste Pexels image URL"
                      value={newImageUrl}
                      onChange={(e) => setNewImageUrl(e.target.value)}
                      bg={inputBg}
                    />
                    <Button
                      onClick={handleAddField}
                      colorPalette="purple"
                      variant="subtle"
                      leftIcon={<LuPlus />}
                    >
                      Add
                    </Button>
                  </HStack>

                  <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} gap={3}>
                    {formData.images.map((url, index) => (
                      <Box
                        key={index}
                        position="relative"
                        borderRadius="md"
                        overflow="hidden"
                        aspectRatio={1}
                        borderWidth="1px"
                        borderColor={borderColor}
                        group
                      >
                        <Image
                          src={url}
                          alt={`Preview ${index}`}
                          w="full"
                          h="full"
                          objectFit="cover"
                        />
                        <Box
                          position="absolute"
                          bottom={1}
                          left={0}
                          right={0}
                          px={1}
                          display="flex"
                          justifyContent="space-between"
                          opacity={0}
                          _groupHover={{ opacity: 1 }}
                          transition="opacity 0.2s"
                        >
                          <IconButton
                            aria-label="Move left"
                            icon={<LuArrowLeft />}
                            size="xs"
                            variant="solid"
                            bg="blackAlpha.700"
                            color="white"
                            onClick={() => moveImage(index, 'left')}
                            isDisabled={index === 0}
                          />
                          <IconButton
                            aria-label="Move right"
                            icon={<LuArrowRight />}
                            size="xs"
                            variant="solid"
                            bg="blackAlpha.700"
                            color="white"
                            onClick={() => moveImage(index, 'right')}
                            isDisabled={index === formData.images.length - 1}
                          />
                        </Box>
                        <IconButton
                          aria-label="Remove image"
                          icon={<LuTrash2 />}
                          size="xs"
                          colorPalette="red"
                          variant="solid"
                          position="absolute"
                          top={1}
                          right={1}
                          onClick={() => handleRemoveImage(index)}
                          opacity={0}
                          _groupHover={{ opacity: 1 }}
                          transition="opacity 0.2s"
                        />
                      </Box>
                    ))}
                  </SimpleGrid>
                </Box>
              </VStack>
            </Dialog.Body>

            <Dialog.Footer borderTopWidth="1px" borderColor={borderColor}>
              <Flex gap={3} justify="flex-end" w="full">
                <Button onClick={onClose} variant="ghost">
                  Cancel
                </Button>
                <Button
                  colorPalette="purple"
                  bg={COLORS.brand.primary}
                  _hover={{ bg: COLORS.brand.hover }}
                  onClick={handleSave}
                  loading={isSaving}
                  px={8}
                >
                  {actionText}
                </Button>
              </Flex>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>

      <ReorderGalleryImages
        isOpen={isReorderOpen}
        onClose={reorderOnClose}
        images={formData.images}
        onSave={handleApplyReorder}
      />
    </Dialog.Root>
  );
}
